/**
 * HTTP transport for browser client
 * Sends events to server endpoint with fallback strategies
 */

import { withRetry } from '../utils/retry';

export interface SendOptions {
  /** Server endpoint URL */
  endpoint: string;
  /** Request timeout in milliseconds (default: 5000) */
  timeout?: number;
  /** Custom headers to include */
  headers?: Record<string, string>;
}

export interface EcommerceItem {
  product_id?: string;
  product_name?: string;
  variant_id?: string;
  sku?: string;
  price?: number;
  quantity?: number;
  category?: string;
  is_swatch?: boolean;
}

export interface ClientEvent {
  /** Event name (e.g., 'page_view', 'add_to_cart', 'purchase') */
  event_name: string;
  /** Event timestamp (Unix seconds) */
  event_time: number;
  /** Unique event ID for deduplication */
  event_id?: string;
  /** Page context */
  context: {
    page_url: string;
    page_title?: string;
    referrer?: string;
    site_type?: 'desktop' | 'mobile' | 'tablet';
    locale?: string;
    timezone?: string;
    viewport_width?: number;
    viewport_height?: number;
  };
  /** Identity data from cookies */
  identity: {
    anonymous_id?: string;
    fbp?: string;
    fbc?: string;
    ttclid?: string;
    ttp?: string;
    gclid?: string;
    ga_client_id?: string;
    ga_session_id?: string;
    epik?: string;
    msclkid?: string;
    criteo_id?: string;
  };
  /** User data for enhanced matching (email, phone, etc.) */
  user_data?: {
    email?: string;
    phone?: string;
    external_id?: string;
    first_name?: string;
    last_name?: string;
  };
  /** User agent */
  user_agent?: string;
  /** Custom event properties */
  properties?: Record<string, unknown>;
  /** Ecommerce data */
  ecommerce?: {
    currency?: string;
    value?: number;
    order_id?: string;
    items?: EcommerceItem[];
  };
}

export interface SendResult {
  success: boolean;
  status?: number;
  error?: string;
}

/**
 * Send event using fetch API with retry on 5xx/network errors
 */
async function sendWithFetch(
  event: ClientEvent,
  options: SendOptions
): Promise<SendResult> {
  try {
    return await withRetry(
      async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          options.timeout ?? 5000
        );

        try {
          const response = await fetch(options.endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...options.headers,
            },
            body: JSON.stringify(event),
            signal: controller.signal,
            keepalive: true, // Allows request to complete even if page unloads
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            return { success: true, status: response.status };
          }

          // 5xx: throw to trigger retry
          if (response.status >= 500) {
            throw Object.assign(
              new Error(`HTTP ${response.status}`),
              { status: response.status }
            );
          }

          // 4xx: return immediately, no retry
          return {
            success: false,
            status: response.status,
            error: `HTTP ${response.status}`,
          };
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      },
      { maxAttempts: 3, initialDelayMs: 100 }
    );
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: false, error: 'Request timeout' };
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Send event using sendBeacon API (for page unload)
 * Note: sendBeacon doesn't return response status
 */
function sendWithBeacon(event: ClientEvent, endpoint: string): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
    return false;
  }

  const blob = new Blob([JSON.stringify(event)], {
    type: 'application/json',
  });

  return navigator.sendBeacon(endpoint, blob);
}

/**
 * Send event to server endpoint
 * Uses fetch by default, falls back to sendBeacon for page unload events
 */
export async function sendEvent(
  event: ClientEvent,
  options: SendOptions
): Promise<SendResult> {
  // Use sendBeacon for page unload scenarios (fire and forget)
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    const sent = sendWithBeacon(event, options.endpoint);
    return { success: sent };
  }

  return sendWithFetch(event, options);
}

/**
 * Queue for batching events (optional, for high-traffic scenarios)
 *
 * @deprecated Server-side batch endpoint is not yet implemented.
 * This class sends events as `{ events: [...] }` but the server currently
 * only accepts single events. Use `sendEvent()` for now.
 * Batch support will be added in a future release.
 */
export class EventQueue {
  private queue: ClientEvent[] = [];
  private options: SendOptions;
  private flushTimeout: ReturnType<typeof setTimeout> | null = null;
  private maxBatchSize: number;
  private flushInterval: number;

  constructor(
    options: SendOptions,
    config?: {
      maxBatchSize?: number;
      flushInterval?: number;
    }
  ) {
    this.options = options;
    this.maxBatchSize = config?.maxBatchSize ?? 10;
    this.flushInterval = config?.flushInterval ?? 5000;

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush(true);
        }
      });

      window.addEventListener('pagehide', () => {
        this.flush(true);
      });
    }
  }

  /**
   * Add event to queue
   */
  add(event: ClientEvent): void {
    this.queue.push(event);

    if (this.queue.length >= this.maxBatchSize) {
      this.flush();
    } else if (!this.flushTimeout) {
      this.flushTimeout = setTimeout(() => this.flush(), this.flushInterval);
    }
  }

  /**
   * Flush all queued events
   */
  async flush(useBeacon = false): Promise<void> {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }

    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    if (useBeacon) {
      // Use sendBeacon for page unload
      const blob = new Blob([JSON.stringify({ events })], {
        type: 'application/json',
      });
      navigator.sendBeacon?.(this.options.endpoint, blob);
    } else {
      // Use fetch for normal scenarios
      try {
        await fetch(this.options.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.options.headers,
          },
          body: JSON.stringify({ events }),
          keepalive: true,
        });
      } catch {
        // Re-queue failed events (optional: implement retry logic)
        this.queue.unshift(...events);
      }
    }
  }
}
